import React, { useEffect, useState } from 'react';
import ApexCharts from 'apexcharts';
import { useAuth } from "../services/auth.service";
import axios from 'axios';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

export default function Survey() {
    const { user } = useAuth();
    const [charts, setCharts] = useState([]);
    const [textResponses, setTextResponses] = useState(null);
    const [counts, setCounts] = useState([]);
    const [questionAnswers, setQuestionAnswers] = useState([]);
    const [isRendered, setIsRendered] = useState(false);

    // Filter states
    const [surveyFilter, setSurveyFilter] = useState(null); // 'total', 'thisYear', 'year', 'thisMonth', 'thisWeek', 'today', 'hour', 'date'
    const [countFilter, setCountFilter] = useState(null);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedDate, setSelectedDate] = useState(null);
    const [countSelectedDate, setCountSelectedDate] = useState(null);
    const [countSelectedYear, setCountSelectedYear] = useState(new Date().getFullYear());

    // Survey management states
    const [surveys, setSurveys] = useState([]);
    const [selectedSurvey, setSelectedSurvey] = useState(null);
    const [surveyId, setSurveyId] = useState('');
    const [showSurveyForm, setShowSurveyForm] = useState(false);
    const [surveyFormData, setSurveyFormData] = useState({ screens: [] });

    // Feedback states
    const [feedbacks, setFeedbacks] = useState([]);
    const [showFeedbackForm, setShowFeedbackForm] = useState(false);
    const [feedbackFormData, setFeedbackFormData] = useState({ name: '', email: '', message: '', rating: 5 });

    // Loading states
    const [loading, setLoading] = useState(false);

    const API_BASE_URL = process.env.REACT_APP_BACKEND_API;

    const getAuthHeaders = () => ({
        "Content-Type": "application/json",
        'x-access-token': user?.accessToken,
    });

    // Fetch survey counts
    const fetchSurveyCounts = async (filter = null, year = null, date = null) => {
        try {
            let url = `${API_BASE_URL}/surveycount`;
            const params = new URLSearchParams();

            if (filter === 'total' || !filter) {
                params.append('total', 'true');
            } else if (filter === 'year' && year) {
                params.append('year', year);
            } else if (filter === 'date' && date) {
                params.append('date', dayjs(date).format('YYYY-MM-DD'));
            } else {
                params.append(filter, 'true');
            }

            if (params.toString()) {
                url += `?${params.toString()}`;
            }

            const response = await axios.get(url, { headers: getAuthHeaders() });
            return response.data;
        } catch (error) {
            console.error('Error fetching survey counts:', error);
            return { count: 0, filter: 'error' };
        }
    };

    // Fetch surveys
    const fetchSurveys = async (filter = null, year = null, date = null) => {
        try {
            let url = `${API_BASE_URL}/surveys`;
            const params = new URLSearchParams();

            if (filter === 'year' && year) {
                params.append('year', year);
            } else if (filter === 'date' && date) {
                params.append('date', dayjs(date).format('YYYY-MM-DD'));
            } else if (filter && filter !== 'total') {
                params.append(filter, 'true');
            }

            if (params.toString()) {
                url += `?${params.toString()}`;
            }

            const response = await axios.get(url, { headers: getAuthHeaders() });
            return response.data;
        } catch (error) {
            console.error('Error fetching surveys:', error);
            return {};
        }
    };

    // Fetch survey by ID
    const fetchSurveyById = async (id) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/surveys/${id}`, { headers: getAuthHeaders() });
            return response.data;
        } catch (error) {
            console.error('Error fetching survey by ID:', error);
            alert('Error al obtener la encuesta: ' + (error.response?.data?.message || error.message));
            return null;
        }
    };

    // Create survey
    const createSurvey = async (surveyData) => {
        try {
            const response = await axios.post(`${API_BASE_URL}/surveys`, surveyData, { headers: getAuthHeaders() });
            alert('Encuesta creada exitosamente');
            setShowSurveyForm(false);
            setSurveyFormData({ screens: [] });
            return response.data;
        } catch (error) {
            console.error('Error creating survey:', error);
            alert('Error al crear la encuesta: ' + (error.response?.data?.message || error.message));
            return null;
        }
    };

    // Update survey
    const updateSurvey = async (id, updateData) => {
        try {
            const response = await axios.put(`${API_BASE_URL}/surveys/${id}`, updateData, { headers: getAuthHeaders() });
            alert('Encuesta actualizada exitosamente');
            if (selectedSurvey?._id === id) {
                setSelectedSurvey(response.data);
            }
            return response.data;
        } catch (error) {
            console.error('Error updating survey:', error);
            alert('Error al actualizar la encuesta: ' + (error.response?.data?.message || error.message));
            return null;
        }
    };

    // Delete survey
    const deleteSurvey = async (id) => {
        if (!window.confirm('¿Está seguro de que desea eliminar esta encuesta?')) {
            return;
        }
        try {
            await axios.delete(`${API_BASE_URL}/surveys/${id}`, { headers: getAuthHeaders() });
            alert('Encuesta eliminada exitosamente');
            if (selectedSurvey?._id === id) {
                setSelectedSurvey(null);
            }
        } catch (error) {
            console.error('Error deleting survey:', error);
            alert('Error al eliminar la encuesta: ' + (error.response?.data?.message || error.message));
        }
    };

    // Fetch all feedbacks
    const fetchFeedbacks = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/feedback`, { headers: getAuthHeaders() });
            setFeedbacks(response.data || []);
        } catch (error) {
            console.error('Error fetching feedbacks:', error);
            alert('Error al obtener feedbacks: ' + (error.response?.data?.message || error.message));
        }
    };

    // Create feedback
    const createFeedback = async (feedbackData) => {
        try {
            // Feedback endpoint doesn't require authentication
            const response = await axios.post(`${API_BASE_URL}/feedback`, feedbackData);
            alert('Feedback enviado exitosamente');
            setShowFeedbackForm(false);
            setFeedbackFormData({ name: '', email: '', message: '', rating: 5 });
            return response.data;
        } catch (error) {
            console.error('Error creating feedback:', error);
            alert('Error al enviar feedback: ' + (error.response?.data?.message || error.message));
            return null;
        }
    };

    // Load data with current filters
    const loadData = async () => {
        setLoading(true);
        try {
            // Fetch counts
            const countBy = [
                { value: 'total', name: 'Total' },
                { value: 'thisYear', name: 'Este año' },
                { value: 'thisMonth', name: 'Este mes' },
                { value: 'thisWeek', name: 'Esta semana' },
                { value: 'today', name: 'Hoy' },
                { value: 'hour', name: 'Última hora' }
            ];

            const countPromises = countBy.map(async (period) => {
                const countData = await fetchSurveyCounts(period.value);
                return { period: period.name, count: countData.count || 0 };
            });

            // Add custom filter count if set
            if (countFilter) {
                let customCount;
                if (countFilter === 'year') {
                    customCount = await fetchSurveyCounts('year', countSelectedYear);
                } else if (countFilter === 'date') {
                    customCount = await fetchSurveyCounts('date', null, countSelectedDate);
                } else {
                    customCount = await fetchSurveyCounts(countFilter);
                }
                countPromises.push({
                    period: countFilter === 'year' ? `Año ${countSelectedYear}` :
                        countFilter === 'date' ? `Fecha ${dayjs(countSelectedDate).format('YYYY-MM-DD')}` :
                        countFilter,
                    count: customCount.count || 0
                });
            }

            const countResults = await Promise.all(countPromises);
            setCounts(countResults);

            // Fetch surveys
            let surveyData;
            if (surveyFilter === 'year') {
                surveyData = await fetchSurveys('year', selectedYear);
            } else if (surveyFilter === 'date') {
                surveyData = await fetchSurveys('date', null, selectedDate);
            } else if (surveyFilter) {
                surveyData = await fetchSurveys(surveyFilter);
            } else {
                surveyData = await fetchSurveys();
            }

            // Process survey data for charts
            if (surveyData && Object.keys(surveyData).length > 0) {
                const newCharts = Object.entries(surveyData).map(([question, answerData], index) => {
                    if (answerData.type === 'Selection') {
                        const series = answerData.sources.map(source => {
                            const count = answerData.answers.filter(answer => answer === source.id).length;
                            return { x: source.title, y: count };
                        });
                        const options = {
                            series: [{
                                name: question,
                                color: '#556ee6',
                                data: series,
                            }],
                            chart: {
                                type: 'bar',
                                id: `chart-${index}`,
                                height: '350px',
                                width: '600px',
                            },
                            plotOptions: {
                                bar: {
                                    horizontal: false,
                                    columnWidth: '55%',
                                    endingShape: 'rounded'
                                }
                            },
                            dataLabels: {
                                enabled: false
                            },
                            xaxis: {
                                categories: answerData.sources.map(source => source.title),
                            },
                            yaxis: {
                                stepSize: 1,
                                title: {
                                    text: 'Respuestas',
                                },
                            },
                            tooltip: {
                                y: {
                                    formatter: function (val) {
                                        return val + " respuestas";
                                    }
                                }
                            },
                            title: {
                                text: question,
                                align: 'left',
                                style: {
                                    fontSize: '12px',
                                    color: '#666'
                                }
                            },
                        };
                        return options;
                    } else {
                        return null;
                    }
                }).filter((chart) => chart !== null);

                setCharts(newCharts);

                const newAnswers = Object.entries(surveyData).map(([question, answerData]) => {
                    return {
                        question,
                        answers: answerData.answers.filter((answer) => answer !== 'Sin respuesta'),
                        contacts: answerData.contacts,
                        numbers: answerData.numbers
                    };
                }).filter((answ) => answ !== null);

                setTextResponses(newAnswers.length > 0 ? newAnswers : null);
            } else {
                setCharts([]);
                setTextResponses(null);
            }
        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Initial load
    useEffect(() => {
        if (user?.accessToken) {
            loadData();
            fetchFeedbacks();
        }
    }, [user]);

    // Re-render charts when they change
    useEffect(() => {
        if (!isRendered && charts.length > 0) {
            // Clear existing charts
            charts.forEach((chartItem) => {
                const element = document.querySelector(`#${chartItem.chart.id}`);
                if (element) {
                    element.innerHTML = '';
                }
            });

            // Render new charts
            charts.forEach((chartItem) => {
                const element = document.querySelector(`#${chartItem.chart.id}`);
                if (element) {
                    const chart = new ApexCharts(element, chartItem);
                    chart.render();
                }
            });
            setIsRendered(true);
        }
    }, [charts]);

    // Handle filter changes
    const handleSurveyFilterChange = (filter) => {
        setSurveyFilter(filter);
        if (filter !== 'year' && filter !== 'date') {
            setSelectedYear(new Date().getFullYear());
            setSelectedDate(null);
        }
    };

    const handleCountFilterChange = (filter) => {
        setCountFilter(filter);
        if (filter !== 'year' && filter !== 'date') {
            setCountSelectedYear(new Date().getFullYear());
            setCountSelectedDate(null);
        }
    };

    const applyFilters = () => {
        setIsRendered(false);
        loadData();
    };

    const handleGetSurveyById = async () => {
        if (!surveyId.trim()) {
            alert('Por favor ingrese un ID de encuesta');
            return;
        }
        const survey = await fetchSurveyById(surveyId.trim());
        if (survey) {
            setSelectedSurvey(survey);
        }
    };

    const handleSubmitSurvey = async (e) => {
        e.preventDefault();
        if (selectedSurvey?._id) {
            await updateSurvey(selectedSurvey._id, surveyFormData);
        } else {
            await createSurvey(surveyFormData);
        }
        loadData();
    };

    const handleSubmitFeedback = async (e) => {
        e.preventDefault();
        await createFeedback(feedbackFormData);
    };

    return (
        <>
            <div className="flex flex-col w-full min-h-screen">
                <header className="flex items-center h-16 px-4 border-b shrink-0 md:px-6">
                    <nav className="flex-col hidden gap-6 text-lg font-medium md:flex md:flex-row md:items-center md:gap-5 md:text-sm lg:gap-6">
                        <a className="flex items-center gap-2 text-lg font-semibold md:text-base" href="#" rel="ugc">
                            <svg xmlns="http://www.w3.org/2000/svg" width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
                                <path d="M3 9h18v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9Z" />
                                <path d="m3 9 2.45-4.9A2 2 0 0 1 7.24 3h9.52a2 2 0 0 1 1.8 1.1L21 9" />
                                <path d="M12 3v6" />
                            </svg>
                            <span className="sr-only">Genesis Panama</span>
                        </a>
                        <a className="font-bold" href="#" rel="ugc">Encuesta de Satisfacción</a>
                        <a className="text-gray-500 dark:text-gray-400" href="/admin/surveyform" rel="ugc">Nueva encuesta</a>
                    </nav>
                    <div className="flex items-center w-full gap-4 md:ml-auto md:gap-2 lg:gap-4">
                        <form className="flex-1 ml-auto sm:flex-initial">
                            <div className="relative">
                                <svg xmlns="http://www.w3.org/2000/svg" width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500 dark:text-gray-400">
                                    <circle cx={11} cy={11} r={8} />
                                    <path d="m21 21-4.3-4.3" />
                                </svg>
                                <input type="search" className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 pl-8 sm:w-[300px] md:w-[200px] lg:w-[300px]" placeholder="Search questionnaires..." />
                            </div>
                        </form>
                        <button className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-10 w-10 rounded-full">
                            <img alt="Avatar" className="rounded-full" height={32} src="/placeholder.svg" style={{ aspectRatio: '32/32', objectFit: 'cover' }} width={32} />
                            <span className="sr-only">Toggle user menu</span>
                        </button>
                    </div>
                </header>

                <main className="flex min-h-[calc(100vh_-_theme(spacing.16))] flex-1 flex-col gap-4 p-4 md:gap-8 md:p-10">
                    {/* Filter Section */}
                    <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-6">
                        <h3 className="text-lg font-semibold mb-4">Filtros de Encuestas</h3>
                        <div className="grid gap-4 md:grid-cols-2">
                            <div>
                                <label className="block text-sm font-medium mb-2">Filtro de Encuestas</label>
                                <div className="flex flex-wrap gap-2 mb-2">
                                    <button
                                        onClick={() => handleSurveyFilterChange(null)}
                                        className={`px-3 py-1 rounded text-sm ${surveyFilter === null ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                                    >
                                        Sin filtro
                                    </button>
                                    <button
                                        onClick={() => handleSurveyFilterChange('thisYear')}
                                        className={`px-3 py-1 rounded text-sm ${surveyFilter === 'thisYear' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                                    >
                                        Este año
                                    </button>
                                    <button
                                        onClick={() => handleSurveyFilterChange('thisMonth')}
                                        className={`px-3 py-1 rounded text-sm ${surveyFilter === 'thisMonth' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                                    >
                                        Este mes
                                    </button>
                                    <button
                                        onClick={() => handleSurveyFilterChange('thisWeek')}
                                        className={`px-3 py-1 rounded text-sm ${surveyFilter === 'thisWeek' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                                    >
                                        Esta semana
                                    </button>
                                    <button
                                        onClick={() => handleSurveyFilterChange('today')}
                                        className={`px-3 py-1 rounded text-sm ${surveyFilter === 'today' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                                    >
                                        Hoy
                                    </button>
                                    <button
                                        onClick={() => handleSurveyFilterChange('hour')}
                                        className={`px-3 py-1 rounded text-sm ${surveyFilter === 'hour' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                                    >
                                        Última hora
                                    </button>
                                    <button
                                        onClick={() => handleSurveyFilterChange('year')}
                                        className={`px-3 py-1 rounded text-sm ${surveyFilter === 'year' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                                    >
                                        Año específico
                                    </button>
                                    <button
                                        onClick={() => handleSurveyFilterChange('date')}
                                        className={`px-3 py-1 rounded text-sm ${surveyFilter === 'date' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                                    >
                                        Fecha específica
                                    </button>
                                </div>
                                {surveyFilter === 'year' && (
                                    <div className="mb-2">
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <input
                                                type="number"
                                                value={selectedYear}
                                                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                                                className="border rounded px-2 py-1"
                                                placeholder="Año"
                                                min="2000"
                                                max={new Date().getFullYear() + 1}
                                            />
                                        </LocalizationProvider>
                                    </div>
                                )}
                                {surveyFilter === 'date' && (
                                    <div className="mb-2">
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DatePicker
                                                label="Fecha"
                                                value={selectedDate ? dayjs(selectedDate) : null}
                                                onChange={(newValue) => setSelectedDate(newValue ? newValue.toDate() : null)}
                                            />
                                        </LocalizationProvider>
                                    </div>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-2">Filtro de Conteos</label>
                                <div className="flex flex-wrap gap-2 mb-2">
                                    <button
                                        onClick={() => handleCountFilterChange('total')}
                                        className={`px-3 py-1 rounded text-sm ${countFilter === 'total' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                                    >
                                        Total
                                    </button>
                                    <button
                                        onClick={() => handleCountFilterChange('thisYear')}
                                        className={`px-3 py-1 rounded text-sm ${countFilter === 'thisYear' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                                    >
                                        Este año
                                    </button>
                                    <button
                                        onClick={() => handleCountFilterChange('year')}
                                        className={`px-3 py-1 rounded text-sm ${countFilter === 'year' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                                    >
                                        Año específico
                                    </button>
                                    <button
                                        onClick={() => handleCountFilterChange('date')}
                                        className={`px-3 py-1 rounded text-sm ${countFilter === 'date' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                                    >
                                        Fecha específica
                                    </button>
                                </div>
                                {countFilter === 'year' && (
                                    <div className="mb-2">
                                        <input
                                            type="number"
                                            value={countSelectedYear}
                                            onChange={(e) => setCountSelectedYear(parseInt(e.target.value))}
                                            className="border rounded px-2 py-1"
                                            placeholder="Año"
                                            min="2000"
                                            max={new Date().getFullYear() + 1}
                                        />
                                    </div>
                                )}
                                {countFilter === 'date' && (
                                    <div className="mb-2">
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DatePicker
                                                label="Fecha"
                                                value={countSelectedDate ? dayjs(countSelectedDate) : null}
                                                onChange={(newValue) => setCountSelectedDate(newValue ? newValue.toDate() : null)}
                                            />
                                        </LocalizationProvider>
                                    </div>
                                )}
                            </div>
                        </div>
                        <button
                            onClick={applyFilters}
                            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                        >
                            Aplicar Filtros
                        </button>
                    </div>


                    {/* Counts Cards */}
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                        {counts.map((count, index) => (
                            <div key={index} className="rounded-lg border bg-card text-card-foreground shadow-sm">
                                <div className="p-6 flex flex-row items-center justify-between pb-2 space-y-0">
                                    <h3 className="whitespace-nowrap tracking-tight text-sm font-medium">
                                        Respuestas {count.period}
                                    </h3>
                                    <svg xmlns="http://www.w3.org/2000/svg" width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-gray-500 dark:text-gray-400">
                                        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                                        <circle cx={9} cy={7} r={4} />
                                        <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                                        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                    </svg>
                                </div>
                                <div className="p-6">
                                    <div className="text-2xl font-bold">
                                        {count.count}
                                    </div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        Respuestas en {count.period}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Charts Section */}
                    <div className="grid gap-4 md:grid-cols-2">
                        <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
                            <div className="p-6 flex flex-row items-center justify-between pb-2 space-y-0">
                                <h3 className="whitespace-nowrap tracking-tight text-sm font-medium">
                                    Respuestas de la encuesta
                                </h3>
                                <svg xmlns="http://www.w3.org/2000/svg" width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-gray-500 dark:text-gray-400">
                                    <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
                                    <path d="M22 12A10 10 0 0 0 12 2v10z" />
                                </svg>
                            </div>
                            <div className="p-6">
                                <div className="w-full aspect-[1/1]">
                                    <div style={{ width: '100%', height: '100%' }}>
                                        <div style={{ position: 'relative' }}>
                                            <div className="relative flex flex-col rounded-xl bg-white bg-clip-border text-gray-700 shadow-md">
                                                <div className="py-6 mt-4 grid place-items-center px-2">
                                                    {charts.map((chart) => (
                                                        <div key={chart.chart.id} id={chart.chart.id} />
                                                    ))}
                                                    {charts.length === 0 && !loading && (
                                                        <p className="text-gray-500">No hay datos de encuestas disponibles</p>
                                                    )}
                                                    {loading && (
                                                        <p className="text-gray-500">Cargando...</p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Text Responses Table */}
                        <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
                            <div className="relative w-full overflow-y-auto">
                                <table className="w-full caption-bottom text-sm">
                                    <thead className="[&_tr]:border-b">
                                        <tr className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
                                            <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0">
                                                Pregunta
                                            </th>
                                            <th className="h-12 px-4 align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 text-left">
                                                Respuesta
                                            </th>
                                            <th className="h-12 px-4 align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 text-left">
                                                Contacto
                                            </th>
                                            <th className="h-12 px-4 align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 text-left">
                                                Número
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="[&_tr:last-child]:border-0">
                                        {textResponses && textResponses.map((textResponse, index) => (
                                            textResponse.answers.map((answer, answerIndex) => (
                                                <tr key={`${index}-${answerIndex}`} className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
                                                    <td className="p-4 align-middle [&:has([role=checkbox])]:pr-0 font-medium">
                                                        {textResponse.question}
                                                    </td>
                                                    <td className="p-4 align-middle [&:has([role=checkbox])]:pr-0">
                                                        {answer}
                                                    </td>
                                                    <td className="p-4 align-middle [&:has([role=checkbox])]:pr-0">
                                                        {textResponse.contacts[answerIndex]}
                                                    </td>
                                                    <td className="p-4 align-middle [&:has([role=checkbox])]:pr-0">
                                                        {textResponse.numbers[answerIndex]}
                                                    </td>
                                                </tr>
                                            ))
                                        ))}
                                        {(!textResponses || textResponses.length === 0) && !loading && (
                                            <tr>
                                                <td colSpan="4" className="p-4 text-center text-gray-500">
                                                    No hay respuestas de texto disponibles
                                                </td>
                                            </tr>
                                        )}
                                        {loading && (
                                            <tr>
                                                <td colSpan="4" className="p-4 text-center text-gray-500">
                                                    Cargando...
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        </>
    );
}
